<template>
  <div class="w-full">
    <div v-if="!isEditMode" class="shadow-none bg-transparent">
      <div class="bg-white px-[12px] md:px-[24px] py-[32px] mt-[16px] rounded-[4px]">
        <div class="flex justify-between items-center">
          <h2 class="font-bold text-[20px] leading-[20px] text-[#2463EB] w-full">
            Sales Order {{ purchaseOrder.orderNumber }}
          </h2>
          <button v-if="
            getStatus(purchaseOrder.status) === 'submitted' &&
            hasPermission('Sales_Order_Update')
          " class="border-[1px] border-[#96a0b5] rounded-[8px] text-[#96a0b5] font-normal text-[14px] border-solid px-[24px] py-[12px] flex items-center gap-[5px] flex items-center gap-[5px]"
            @click="toggleEditMode('edit')">
            <img src="/assets/icon/edit-2.svg" alt="" />
            Edit
          </button>
        </div>
        <div class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Order for
          </h2>
          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">{{ purchaseOrder.parent.name }}</span>
        </div>
        <div v-if="purchaseOrder.contact.phoneNumber"
          class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Phone Number
          </h2>
          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">{{ purchaseOrder.parent.phoneNumber
          }}</span>
        </div>
        <div class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Delivery Address
          </h2>
          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">{{ purchaseOrder.deliveryAddress
          }}</span>
        </div>
        <div class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Total Order Amount
          </h2>
          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">&#8358;{{ formatMoney(total)
          }}</span>
        </div>
        <div class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Creation Date
          </h2>
          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">{{ formatDate(purchaseOrder.submittedAt)
          }}</span>
        </div>
        <div v-if="purchaseOrder.approvedAt"
          class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Approval Date
          </h2>
          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">{{ formatDate(purchaseOrder.approvedAt)
          }}</span>
        </div>
        <div class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Order Status
          </h2>
          <span class="flex items-center gap-[4px] max-w-max w-full" :class="[
            'badge',
            {
              'badge-success': getStatus(purchaseOrder.status) === 'approved',
            },
            {
              'badge-danger': getStatus(purchaseOrder.status) === 'rejected',
            },
            {
              'badge-secondary': getStatus(purchaseOrder.status) === 'closed',
            },
            {
              'badge-warning':
                getStatus(purchaseOrder.status) === 'submitted',
            },
          ]">
            <span class="w-[10px] h-[10px] rounded-[1px]" :style="{
              backgroundColor:
                getStatus(purchaseOrder.status) === 'approved'
                  ? '#16a249'
                  : getStatus(purchaseOrder.status) === 'submitted'
                    ? '#FFA500'
                    : getStatus(purchaseOrder.status) === 'closed'
                      ? '#ccc'
                      : getStatus(purchaseOrder.status) === 'rejected'
                        ? '#e42728'
                        : '',
            }"></span>
            <span>
              {{
                getStatus(purchaseOrder.status) === "approved"
                  ? "Approved"
                  : getStatus(purchaseOrder.status) === "closed"
                    ? "Completed"
                    : getStatus(purchaseOrder.status) === "submitted"
                      ? "Submitted"
                      : getStatus(purchaseOrder.status) === "rejected"
                        ? "Rejected"
                        : getStatus(purchaseOrder.status)
              }}
            </span>
          </span>
        </div>
        <div class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            SAP Number
          </h2>

          <span class="font-medium text-[16px] text-[#353535] leadin-[19.36px]">{{ getSapInfo(purchaseOrder) }}</span>
        </div>
        <div v-if="balance" class="flex flex-col md:flex-row items-start gap-2 md:items-center md:gap-4 mt-2">
          <h2 class="font-normal text-[16px] text-[#96a0b5] max-w-[170px] w-full">
            Distributor Available Balance
          </h2>
          <span :class="[
            'font-medium text-[16px] leading-[19.36px]',
            balance.endsWith('-') ? 'text-green-500' : 'text-red-500'
          ]">
            ₦{{ formatBalance(balance) }}
          </span>
        </div>
        <div class="flex items-center gap-[8px] mt-[16px]" v-if="getStatus(purchaseOrder.status) === 'submitted'">
          <button v-if="hasPermission('Sales_Order_Process')" @click="approveOrder" type="button"
            class="border-[1px] bg-[#2463eb] rounded-[8px] text-white border-solid px-[16px] sm:px-[24px] text-[12px] md:text-[14px] py-[12px] outline-none focus:outline-none">
            <i v-if="loader.approve" class="fas fa-spinner fa-spin mr-2"></i>

            {{ loader.approve ? "Busy..." : "Approve Order" }}
          </button>
          <button v-if="hasPermission('Sales_Order_Process')" @click="rejectOrder" type="button"
            class="border border-solid border-[#7367F0] bg-[#7367F0] rounded-[8px] text-white px-[16px] sm:px-[24px] text-[12px] md:text-[14px] py-[12px] outline-none focus:outline-none">
            <i v-if="loader.reject" class="fas fa-spinner fa-spin mr-2"></i>

            {{ loader.reject ? "Busy..." : "Reject Order" }}
          </button>
        </div>
      </div>
      <div class="bg-white px-[24px] py-[32px] mt-[16px] rounded-[4px]">
        <ItemDetails :items="PurchaseOrder.details" :isView="true" :closeModal="closeModal"
          :purchaseOrder="purchaseOrder" :justOpenQuantities="justOpenQuantities" />
      </div>
      <SalesOrderItem v-if="addedItems.length > 0" :items="addedItems" :isView="true" />
    </div>

    <div v-if="isEditMode">
      <EditPurchaseOrder :orderId="orderId" :toggleEditMode="toggleEditMode" :closeModal="closeModal"
        :manufacturerProducts="manufacturerProducts" :PurchaseOrder="PurchaseOrder" />
    </div>
    <ApproveModal :orderId="orderId" :purchaseOrder="purchaseOrder" :isBusy="isBusy" :closeModal="closeModal" />
    <RejectModal :orderId="orderId" :purchaseOrder="purchaseOrder" :isBusy="isBusy" :closeModal="closeModal" />
  </div>
</template>

<script>
import moment from "moment";
import { helper } from "@/helper";
import Amount from "../../components/SettingsPurchaseOrder/Amount";
import Quantity from "../../components/SettingsPurchaseOrder/Quantity";
import ItemDetails from "../../components/SettingsPurchaseOrder/PurchaseOrderItemDetails";
import SalesOrderItem from "../../components/SettingsPurchaseOrder/SalesOrderItem.vue";
import EditPurchaseOrder from "./EditPurchaseOrder.vue";
import ApproveModal from "./ApproveModal.vue";
import RejectModal from "./RejectOrderItem.vue";
import SystemPermissions from "@/permissions";

export default {
  mixins: [SystemPermissions.Mixin],
  components: {
    Amount,
    Quantity,
    ItemDetails,
    SalesOrderItem,
    EditPurchaseOrder,
    ApproveModal,
    RejectModal,
  },
  props: [
    "purchaseId",
    "closeModal",
    "type",
    "toggleEditMode",
    "PurchaseOrder",
    "balance"
  ],
  data() {
    return {
      isBusy: false,
      isBusyModal: false,
      loader: { approve: false, reject: false, update: false },
      isActivityLoading: false,
      activities: [],
      orderId: "",
      // balance: "",
      manufacturerProducts: [],
      filteredProducts: [],
      purchaseOrder: {
        parent: {},
        contact: {},
      },
      addedOrderDetails: [],
      stageCautionData: {},
      orderItems: [],
      addedItems: [],
      openQuantities: [],
      justOpenQuantities: [],
    };
  },

  computed: {
    user: function () {
      return this.$store.state.currentUser;
    },
    isEditMode() {
      if (this.type == "edit") {
        return true;
      }
      return false;
    },
    subTotal() {
      let result = 0;
      this.addedOrderDetails.forEach((item) => {
        result += item.price * item.quantity;
      });
      return result.toFixed(2);
    },

    total() {
      const discount = parseFloat(this.purchaseOrder.discountAmount) || 0;
      const result = this.subTotal - discount;
      return result.toFixed(2);
    },
  },

  created() {
    this.orderId = this.purchaseId;
    this.getOrderItems();
    this.editPurchaseOrder();
    this.getActivies();
    this.getOpenQuantities();
    // this.getDistributorCreditInformation()
  },
  async mounted() {
    this.justOpenQuantities = await this.getOpenQuantities();
  },

  methods: {
    formatBalance(val) {
      if (!val) return '';
      const isNegative = val.endsWith('-');
      const cleanVal = isNegative ? val.slice(0, -1) : val;
      return parseFloat(cleanVal).toLocaleString('en-NG', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    getSapInfo(order) {
      if (!order.id) {
        return "";
      }
      const orderStatus = order.status.toLocaleLowerCase();
      if (orderStatus === "rejected" || orderStatus === "cancelled") {
        return "***";
      }
      if (orderStatus === "submitted" || orderStatus === "draft") {
        return "Awaiting approval";
      }
      return order.sapSalesOrderNumber || "No SAP number";
    },
    getStatus(status) {
      return status ? status.toLowerCase() : "";
    },
    async getOpenQuantities() {
      try {
        const res = await this.$http.get(
          `/PurchaseOrders/${this.orderId}/getOpenQuantities`
        );

        if (res && res.data && res.data.orderQuantityStatuses) {
          this.openQuantities = res.data;
          const justOpenQuantities = this.openQuantities.orderQuantityStatuses.map(
            (status) => status.openQuantity
          );
          return justOpenQuantities;
        }
      } catch (err) {
        console.error("Failed to fetch open quantities:", err);
      }
    },
    async updateOrder() {
      // eslint-disable-next-line no-undef
      $("#mod-update-status").modal("show");
      this.closeModal();
    },
    async approveOrder() {
      // eslint-disable-next-line no-undef
      $("#mod-approve").modal("show");
    },
    async rejectOrder() {
      // eslint-disable-next-line no-undef
      $("#mod-reject").modal("show");
    },
    async getActivies() {
      this.isActivityLoading = true;
      try {
        const response = await this.$http.get(
          `/Activity/${this.purchaseOrder.id}/${1}`
        );

        if (response.ok) {
          this.activities = response.body;
        }

        this.isActivityLoading = false;
      } catch (error) {
        this.isActivityLoading = false;
      }
    },
    editOrder() {
      this.isEditMode = !this.isEditMode;
      this.closeModal();
    },
    async editPurchaseOrder() {
      this.isBusy = true;
      try {
        const response = await this.$http.get(
          "/PurchaseOrders/edit/" + this.orderId
        );

        if (response.ok) {
          this.purchaseOrder = response.body;
          this.prepareDetailSet(this.purchaseOrder.details);
          this.prepareItemSet(this.purchaseOrder.orderItems);
          this.getManufacturerProducts();
        }

        this.isBusy = false;
      } catch (error) {
        this.isBusy = false;
      }
    },

    calculatePercentageAmount(percent, amount) {
      return (percent / 100) * amount;
    },

    formatMoney(val) {
      return helper.formatMoney(val);
    },

    formatDate(date) {
      if (date) return moment(date).format("lll");
      else return "";
    },

    async getManufacturerProducts() {
      this.isBusyModal = true;
      try {
        const response = await this.$http.get(
          "/Products/getListNoPagingForManufacturer?active=true" +
          (this.user.parentId ? "&parentId=" + this.user.parentId : "")
        );
        if (response.ok) {
          this.manufacturerProducts = this.filteredProducts = response.body;
          this.performFilter();
        }

        this.isBusyModal = false;
      } catch (error) {
        this.isBusyModal = false;
      }
    },

    performFilter() {
      this.addedOrderDetails.forEach((element) => {
        this.filteredProducts = this.filteredProducts.filter(
          (product) => product.id !== element.id
        );
      });
    },

    prepareItem(product, newItem) {
      console.log(product);
      console.log(newItem);
      const preparedItem = {
        quantity: newItem.quantity,
        description: `${product.name} (${product.code}) - ${product.retailPrice}/${product.unit}`,
        price: product.retailPrice,
        id: product.id,
      };

      this.addedOrderDetails.push(preparedItem);

      // eslint-disable-next-line no-undef
      $("#mod-product-add").modal("hide");
    },

    prepareItemSet(orderItems) {
      this.addedItems = [];
      orderItems.forEach((element) => {
        this.orderItems.forEach((sub) => {
          if (element.orderItemId === sub.id) this.addedItems.push(sub);
        });
      });
    },

    prepareDetailSet(productsDetails) {
      this.addedOrderDetails = [];
      productsDetails.forEach((element) => {
        const product = this.PurchaseOrder.details.find(
          (v) => v.productId == element.productId
        );
        const preparedItem = {
          quantity: element.quantity,
          price: element.price,
          id: element.productId,
          description: element.description,
          productCode: product.code || "",
          productName: product.name,
        };

        this.addedOrderDetails.push(preparedItem);
      });
    },

    async getOrderItems() {
      this.isBusyModal = true;
      try {
        const response = await this.$http.get("/PurchaseOrders/orderItem/list");
        if (response.ok) {
          this.orderItems = response.body;
        }

        this.isBusyModal = false;
      } catch (error) {
        this.isBusyModal = false;
      }
    },
  },
};
</script>
